// addUniqueConstraint — DdlEdit builder + commit dispatcher for the
// `applyDdlEdit({ op: 'add-unique-constraint', unique })` op (DSE2 §3.B3).
//
// PRD: Ralph/VqeDdlEdit/PRD.md § Slice B3.
//
// Per-engine support (mirrors DSE2's `synthesizeAddUniqueConstraint` rules):
//   - sqlite: rejected on ALTER (recreate path opt-in lives in Slice H3).
//   - mssql: optional `clustered: 'clustered' | 'nonclustered'`.
//   - postgres: optional `includeColumns` (11+); optional `nullsDistinct`
//     (15+); optional `deferrable`.
//   - oracle: optional `tablespace`; optional `deferrable`.
//   - mysql: columns + name only (no per-engine extras).

import type {
  DdlEdit,
  UniqueConstraintDef,
} from '../../../../sql-resolver/types/ddlEdit';
import type { SqlEngine } from '../../../../sql-grammar/model';
import { commitDdlEdit } from '../commitDdlEdit';
import { toCommitCtx, type VqeDdlEditContext } from '../editContext';

export type AddUniqueConstraintArgs = {
  readonly unique: UniqueConstraintDef;
};

export function buildAddUniqueConstraintEdit(args: AddUniqueConstraintArgs): DdlEdit {
  return { op: 'add-unique-constraint', unique: args.unique };
}

export async function commitAddUniqueConstraint(
  ctx: VqeDdlEditContext,
  args: AddUniqueConstraintArgs,
): Promise<void> {
  const edit = buildAddUniqueConstraintEdit(args);
  await commitDdlEdit(edit, toCommitCtx(ctx));
}

export function isAddUniqueConstraintSupported(engine: SqlEngine): boolean {
  return engine !== 'sqlite';
}

export function getAddUniqueConstraintUnsupportedTooltip(engine: SqlEngine): string {
  if (engine === 'sqlite') {
    return (
      'SQLite does not support ALTER TABLE … ADD CONSTRAINT UNIQUE. '
      + 'Declare the unique constraint inline at CREATE TABLE time, or '
      + 'recreate the table with the constraint — use “Edit as SQL” to '
      + 'write the change.'
    );
  }
  return '';
}

export function isClusteredSupported(engine: SqlEngine): boolean {
  return engine === 'mssql';
}

export function isIncludeColumnsSupported(engine: SqlEngine): boolean {
  return engine === 'postgres';
}

export function isNullsDistinctSupported(engine: SqlEngine): boolean {
  return engine === 'postgres';
}

export function isTablespaceSupported(engine: SqlEngine): boolean {
  return engine === 'oracle';
}

export function isDeferrableSupported(engine: SqlEngine): boolean {
  return engine === 'postgres' || engine === 'oracle';
}

export function getEngineFieldUnsupportedTooltip(
  engine: SqlEngine,
  field: 'clustered' | 'includeColumns' | 'nullsDistinct' | 'tablespace' | 'deferrable',
): string {
  switch (field) {
    case 'clustered':
      return 'CLUSTERED / NONCLUSTERED is a SQL Server-only UNIQUE modifier.';
    case 'includeColumns':
      return 'INCLUDE columns require PostgreSQL 11+ and are unsupported on other engines.';
    case 'nullsDistinct':
      return 'NULLS DISTINCT / NULLS NOT DISTINCT requires PostgreSQL 15+ and is unsupported on other engines.';
    case 'tablespace':
      return 'USING INDEX TABLESPACE is an Oracle-only UNIQUE modifier.';
    case 'deferrable':
      return 'DEFERRABLE INITIALLY is supported on PostgreSQL + Oracle only.';
  }
  void engine;
  return '';
}

/** Pre-flight validator mirroring DSE2's `validateUq` rules so the form can
 *  surface engine errors before the synthesizer would throw. Returns null
 *  when the spec is acceptable. */
export function validateUniqueConstraintForEngine(
  engine: SqlEngine,
  uq: UniqueConstraintDef,
): string | null {
  if (engine === 'sqlite') return getAddUniqueConstraintUnsupportedTooltip(engine);
  if (uq.columns.length === 0) {
    return 'UNIQUE constraint requires at least one column.';
  }
  if (engine !== 'mssql' && uq.clustered !== undefined) {
    return getEngineFieldUnsupportedTooltip(engine, 'clustered');
  }
  if (
    engine !== 'postgres'
    && uq.includeColumns !== undefined
    && uq.includeColumns.length > 0
  ) {
    return getEngineFieldUnsupportedTooltip(engine, 'includeColumns');
  }
  if (engine !== 'postgres' && uq.nullsDistinct !== undefined) {
    return getEngineFieldUnsupportedTooltip(engine, 'nullsDistinct');
  }
  if (engine !== 'oracle' && uq.tablespace !== undefined && uq.tablespace !== '') {
    return getEngineFieldUnsupportedTooltip(engine, 'tablespace');
  }
  if (engine !== 'oracle' && engine !== 'postgres' && uq.deferrable !== undefined) {
    return getEngineFieldUnsupportedTooltip(engine, 'deferrable');
  }
  return null;
}

/** PostgreSQL 15+ note for the form — surfaces in the Nulls Distinct section
 *  on postgres so users on older clusters know why the synthesizer might still
 *  reject (the runtime engine itself rejects pre-15). */
export function getNullsDistinctEngineVersionNote(engine: SqlEngine): string | null {
  if (engine === 'postgres') {
    return 'NULLS DISTINCT / NOT DISTINCT requires PostgreSQL 15+.';
  }
  return null;
}
